import type { Maybe } from '@/helpers/types';
import type { PosterTitle } from '@/interfaces/components/poster/PosterGraphql';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TrackingSpecs } from '@/libraries/tracking';
import type { MaybeRefOrGetter } from '@vueuse/core';

import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';

import { createSpecTracker, defineSpec } from '@/libraries/tracking';

import { toValue } from '@vueuse/core';
import { computed } from 'vue';

const specs = {
	// Impression
	impression: {
		category: 'international_offers_feature',
		action: 'impression',
		label: 'international_offers',
		nonInteraction: true,
	},

	// VPN Clickout
	vpnClickout: defineSpec<{
		label: string;
	}>()({
		category: 'vpn',
		action: 'click',
		property: 'ExpressVPN',
	}),

	// VPN Offer Impression
	vpnOfferImpression: defineSpec<{
		label: string;
	}>()({
		category: 'vpn',
		action: 'impression',
		property: 'ExpressVPN',
		nonInteraction: true,
	}),

	// Offer impression
	// + clickout context - placement: international_offers; NEW FIELDS: appLocale + appLanguage (= of the actual clickout)
	offerImpression: {
		category: 'offer',
		action: 'impression',
		nonInteraction: true,
	},

	// Country dropdown expand
	countryDropdownExpand: defineSpec<{
		property: string;
		value: 0 | 1;
	}>()({
		category: 'userinteraction',
		action: 'toggle_vpn_country',
		label: 'international_offers',
	}),

	// Streaming service dropdown
	streamingServiceDropdown: {
		category: 'userinteraction',
		action: 'provider_selector_clicked',
		label: 'international_offers',
	},

	// Service modal opened
	// - fires together with provider_selector_clicked
	serviceModalOpened: {
		category: 'userinteraction',
		action: 'modal_opened',
		label: 'international_offers',
	},

	// Streaming service selected
	// + package context
	streamingServiceSelected: defineSpec<{
		property: string; // provider_id: cast to string
		value: 0 | 1;
	}>()({
		category: 'userinteraction',
		action: 'package_toggled',
		label: 'international_offers',
	}),

	// Service modal closed
	// + all selected package IDs in cc_package
	serviceModalClosed: {
		category: 'userinteraction',
		action: 'modal_closed',
		label: 'international_offers',
	},

	// Language dropdown
	languageDropdown: {
		category: 'userinteraction',
		action: 'language_selector_clicked',
		label: 'international_offers',
	},

	// Language modal opened
	// - fires together with language_selector_clicked
	languageModalOpened: {
		category: 'userinteraction',
		action: 'modal_opened',
		label: 'international_offers',
	},

	// Language selected
	languageSelected: defineSpec<{
		property: string; // language_code
		value: 0 | 1;
	}>()({
		category: 'userinteraction',
		action: 'language_selector_toggled',
		label: 'international_offers',
	}),

	// Language modal closed
	languageModalClosed: defineSpec<{
		property: string; // all language_codes as string
	}>()({
		category: 'userinteraction',
		action: 'modal_closed',
		label: 'international_offers',
	}),

	// Show more clicked
	showMoreClicked: {
		category: 'userinteraction',
		action: 'show_more_clicked',
		label: 'international_offers',
	},

	// Show all offers within an expanded country
	showAllOffersWithinCountry: {
		category: 'userinteraction',
		action: 'show_all_offers_clicked',
		label: 'international_offers',
	},
} satisfies TrackingSpecs;

export function useInternationalOffersTracking(options: { title: MaybeRefOrGetter<Maybe<TitleDetail | PosterTitle>> }) {
	const titleContext = computed(() => {
		const title = toValue(options.title);
		if (title == null) return null;

		const objectId = title.__typename === 'Season' ? title.show.objectId : title.objectId;
		const objectType = title.__typename === 'Season' ? title.show.objectType : title.objectType;
		const seasonNumber = title.__typename === 'Season' ? title.content?.seasonNumber : undefined;

		return new SnowplowTitleContextGraphql(objectId, objectType, seasonNumber);
	});

	const trackInternationalOffers = createSpecTracker({ specs, baseContexts: () => [titleContext.value] });

	return { trackInternationalOffers };
}
